import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useWeb3Context } from '../../contexts/web3Context'
import { alert } from '../Alert/Alert'
import ConfirmModal from '../confirm-modal'
import { getSharedPvPSocket } from '../../socket/pvpSharedSocket'
import {
  setFriendRequestDeclinedNotice,
  setFriendRequestRespondBusy,
  setIncomingFriendRequest,
} from '../../common/state/pvp/reducer'
import type { RootState } from '../../store'
import {
  postPvpFriendAccept,
  postPvpFriendDecline,
} from '../../common/api'
import {
  attachFriendRequestSocketListeners,
  detachFriendRequestSocketListeners,
  hydrateIncomingFriendRequest,
} from '../../common/friendRequestSocketBridge'

export const GlobalFriendRequestHost: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { address } = useWeb3Context()
  const incoming = useSelector((s: RootState) => s.app.pvp.incomingFriendRequest)
  const declinedNotice = useSelector((s: RootState) => s.app.pvp.friendRequestDeclinedNotice)
  const respondBusy = useSelector((s: RootState) => s.app.pvp.friendRequestRespondBusy)

  React.useEffect(() => {
    if (!address) {
      dispatch(setIncomingFriendRequest(null))
      dispatch(setFriendRequestDeclinedNotice(null))
      dispatch(setFriendRequestRespondBusy(false))
    }
  }, [address, dispatch])

  React.useEffect(() => {
    if (!address) return

    void hydrateIncomingFriendRequest(address, { force: true })

    const onFocus = () => {
      void hydrateIncomingFriendRequest(address)
    }
    window.addEventListener('focus', onFocus)

    const socket = getSharedPvPSocket(address)
    const onConnect = () => {
      socket.emit('join-wallet-room', { walletAddress: address })
      socket.emit('user:online', { walletAddress: address })
      attachFriendRequestSocketListeners(socket)
      void hydrateIncomingFriendRequest(address, { force: true })
    }

    socket.on('connect', onConnect)
    if (socket.connected) onConnect()

    return () => {
      window.removeEventListener('focus', onFocus)
      socket.off('connect', onConnect)
      detachFriendRequestSocketListeners(socket)
    }
  }, [address, dispatch])

  const handleAccept = React.useCallback(async () => {
    if (!address || !incoming || respondBusy) return
    dispatch(setFriendRequestRespondBusy(true))
    const res = await postPvpFriendAccept({
      walletAddress: address,
      requestId: incoming.requestId,
    })
    dispatch(setFriendRequestRespondBusy(false))
    if (!res.success) {
      void alert.open(res.message || t('pvp.friendRequestAcceptFailed'))
      return
    }
    dispatch(setIncomingFriendRequest(null))
    void alert.open(
      t('pvp.friendRequestAcceptedToast', { name: incoming.requesterNickname }),
    )
  }, [address, dispatch, incoming, respondBusy, t])

  const handleDecline = React.useCallback(async () => {
    if (!address || !incoming || respondBusy) return
    dispatch(setFriendRequestRespondBusy(true))
    const res = await postPvpFriendDecline({
      walletAddress: address,
      requestId: incoming.requestId,
    })
    dispatch(setFriendRequestRespondBusy(false))
    if (!res.success) {
      void alert.open(res.message || t('pvp.friendRequestDeclineFailed'))
      return
    }
    dispatch(setIncomingFriendRequest(null))
  }, [address, dispatch, incoming, respondBusy, t])

  const dismissDeclinedNotice = React.useCallback(() => {
    dispatch(setFriendRequestDeclinedNotice(null))
  }, [dispatch])

  if (!address) return null

  const incomingDescription = incoming
    ? t('pvp.friendRequestIncomingDescription', { name: incoming.requesterNickname })
    : ''

  const declinedDescription = declinedNotice
    ? t('pvp.friendRequestDeclinedDescription', { name: declinedNotice.recipientNickname })
    : ''

  return (
    <>
      <ConfirmModal
        open={!!incoming}
        title={t('pvp.friendRequestIncomingTitle')}
        message={incomingDescription}
        confirmText={t('pvp.friendRequestAccept')}
        cancelText={t('pvp.friendRequestDecline')}
        onConfirm={() => void handleAccept()}
        onClose={() => void handleDecline()}
        isProcessing={respondBusy}
        zIndex={14000}
        closeOnBackdropClick={false}
        disableEscapeKeyDown
      />
      <ConfirmModal
        open={!!declinedNotice}
        title={t('pvp.friendRequestDeclinedTitle')}
        message={declinedDescription}
        confirmText={t('common.ok')}
        onConfirm={dismissDeclinedNotice}
        onClose={dismissDeclinedNotice}
        showCancelButton={false}
        zIndex={14000}
      />
    </>
  )
}

export default GlobalFriendRequestHost
